import { useState, FC, useMemo, useEffect } from "react";
import classNames from "classnames";
import { PaymentMode, ShipmentInterface } from "../../interfaces";
import { getShipment } from "../../services";
import { FormTextInput } from "../../components/FormFields";
import { PrimaryButton } from "../../components/Buttons";
import PaginationFooter from "../../components/PaginationFooter";
import LoadingIcon from "../../components/LoadingIcon";
import PendingShipmentItem from "./PendingShipmentItem";

const ROWS_PER_PAGE = 20;

const message = (begin: number, end: number, total: number) => {
  return (
    <p className="text-sm text-gray-700">
      Mostrando resultados <span className="font-medium">{begin}</span> a{" "}
      <span className="font-medium">{end}</span> de los
      <span className="font-medium"> {total}</span> más relevantes.
    </p>
  );
};

interface ShipmentTableSearchProps {
  forceSearch?: boolean;
}
const ShipmentTableSearch: FC<ShipmentTableSearchProps> = ({ forceSearch }) => {
  const [query, setQuery] = useState("");
  const [loader, setLoader] = useState(false);
  const [itemOffset, setItemOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [shipments, setShipments] = useState<ShipmentInterface[]>([]);

  const paginatedShipments = useMemo(() => {
    return shipments.slice(itemOffset, itemOffset + ROWS_PER_PAGE);
  }, [shipments, itemOffset]);

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * ROWS_PER_PAGE) % shipments.length;
    setItemOffset(newOffset);
  };

  const onSearchShipments = async () => {
    setLoader(true);

    const response = await getShipment(query);
    if (!response || response.paymentMode === PaymentMode.EXEMPT) {
      setShipments([]);
      setLoader(false);
      return;
    }

    setShipments([response]);
    setLoader(false);
  };

  useEffect(() => {
    setTotalPages(Math.ceil(shipments.length / ROWS_PER_PAGE));
  }, [itemOffset, shipments]);

  useEffect(() => {
    if (forceSearch) {
      onSearchShipments();
    }
  }, [forceSearch]);

  return (
    <div className="flex flex-col max-w-full flex-1">
      {/* Filters */}
      <div className="flex flex-1 flex-col 2xl:flex-row relative gap-2 2xl:gap-10">
        <div className="flex items-center gap-4 sm:gap-10">
          <div className="flex flex-1 flex-col lg:min-w-[30rem]">
            <FormTextInput
              value={query}
              label=""
              id="shipments-search-query"
              onClick={onSearchShipments}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por número de guía"
              data-te-toggle="tooltip"
              title="Buscar por número de guía"
            />
          </div>

          <div className="items-end justify-end flex h-full 2xl:hidden">
            <div>
              <PrimaryButton onClick={onSearchShipments}>Buscar</PrimaryButton>
            </div>
          </div>
        </div>

        <div className="items-end justify-end hidden 2xl:flex">
          <div>
            <PrimaryButton onClick={onSearchShipments}>Buscar</PrimaryButton>
          </div>
        </div>
      </div>

      {/* Shipments */}
      <div
        className={classNames(
          "w-auto mt-8",
          (shipments.length === 0 || loader) && "hidden"
        )}
      >
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-xs">
                  GUÍA
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  REMITENTE
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  DESTINATARIO
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  CARACTERÍSTICAS
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  FACTURA
                </th>

                <th className="text-left px-4 py-2 font-semibold text-xs">
                  UBICACIÓN
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedShipments.map((shipment, index: number) => (
                <PendingShipmentItem
                  showLocation
                  key={index}
                  index={index}
                  shipment={shipment}
                />
              ))}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          totalPages={totalPages}
          itemsOffSet={itemOffset}
          rowCounts={ROWS_PER_PAGE}
          totalItems={shipments.length}
          handlePageClick={handlePageClick}
          message={shipments.length === 100 ? message : undefined}
        />
      </div>

      <div
        className={classNames(
          "flex flex-1 items-center justify-center min-h-[10rem]",
          (shipments.length > 0 || loader) && "hidden"
        )}
      >
        <p className="text-gray-400">No hay guías en esta lista</p>
      </div>

      <div
        className={classNames(
          "flex items-center justify-center w-full p-8",
          !loader && "hidden"
        )}
      >
        <LoadingIcon size="2rem" />
      </div>
    </div>
  );
};

export default ShipmentTableSearch;
